import React, { useState, useEffect } from "react";
import TrainingAPI from "../services/TrainingAPI";
import LoadingSpinner from "../common/LoadingSpinner";
import "./css/Training.css";
import "./css/Quiz.css";

/**
 * Quiz - Interactive quiz component for training assessments
 *
 * Features:
 * - Multiple choice, true/false, and multiple select questions
 * - Timer (optional)
 * - Progress indicator
 * - Results display with explanations
 * - Pass/fail feedback
 */
const Quiz = ({ trainingUuid, sessionId, onComplete, onBack }) => {
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [startTime] = useState(new Date());
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    loadQuiz();
  }, [trainingUuid]);

  // Timer countdown
  useEffect(() => {
    if (!quiz?.timeLimitMinutes || result || error) return;

    const deadline = new Date(
      startTime.getTime() + quiz.timeLimitMinutes * 60000,
    );

    let autoSubmitted = false;
    const interval = setInterval(() => {
      const now = new Date();
      const remaining = Math.max(0, Math.floor((deadline - now) / 1000));
      setTimeRemaining(remaining);

      if (remaining === 0 && !autoSubmitted) {
        // Auto-submit when time runs out (only once)
        autoSubmitted = true;
        clearInterval(interval);
        handleSubmit();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [quiz, result, error, startTime]);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      setError(null);

      // First get quiz metadata
      const quizInfoRes = await TrainingAPI.getQuizForTraining(trainingUuid);

      if (quizInfoRes.success && quizInfoRes.quiz) {
        // Then get quiz with questions
        const quizRes = await TrainingAPI.getQuizWithQuestions(
          quizInfoRes.quiz.quizId,
        );

        if (quizRes.success) {
          setQuiz(quizRes.quiz);
          setQuestions(quizRes.quiz.questions || []);

          // Initialize time remaining
          if (quizRes.quiz.timeLimitMinutes) {
            setTimeRemaining(quizRes.quiz.timeLimitMinutes * 60);
          }
        }
      }
    } catch (err) {
      console.error("[Quiz] Error loading quiz:", err);
      setError("Failed to load quiz. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionUuid, selectedValue) => {
    const question = questions.find((q) => q.uuid === questionUuid);

    if (question?.questionType === "MULTIPLE_SELECT") {
      // For multiple select, toggle the selection
      const currentAnswers = answers[questionUuid]
        ? answers[questionUuid].split(",")
        : [];
      const index = currentAnswers.indexOf(selectedValue);

      if (index > -1) {
        currentAnswers.splice(index, 1);
      } else {
        currentAnswers.push(selectedValue);
      }

      setAnswers({
        ...answers,
        [questionUuid]: currentAnswers.filter((a) => a).join(","),
      });
    } else {
      // For single select
      setAnswers({
        ...answers,
        [questionUuid]: selectedValue,
      });
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    // Prevent concurrent or repeated submissions
    if (submitting) return;

    try {
      setSubmitting(true);

      const timeSpent = Math.floor((new Date() - startTime) / 1000);

      const response = await TrainingAPI.submitQuiz({
        sessionId: sessionId,
        quizId: quiz.quizId,
        answers: answers,
        timeSpentSeconds: timeSpent,
      });

      if (response.success) {
        setResult(response.result);
      }
    } catch (err) {
      console.error("[Quiz] Error submitting quiz:", err);
      const errorMessage = err?.message || err?.response?.data?.message || "";
      if (errorMessage.toLowerCase().includes("maximum attempts")) {
        setError(
          "You have reached the maximum number of attempts for this quiz.",
        );
      } else {
        setError("Failed to submit quiz. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const isOptionSelected = (questionUuid, optionId) => {
    const answer = answers[questionUuid];
    if (!answer) return false;

    const question = questions.find((q) => q.uuid === questionUuid);
    if (question?.questionType === "MULTIPLE_SELECT") {
      return answer.split(",").includes(optionId);
    }
    return answer === optionId;
  };

  const getOptionClass = (question, option, showResults) => {
    if (!showResults) {
      return isOptionSelected(question.uuid, option.id) ? "selected" : "";
    }

    // Show results
    const isCorrect =
      question.questionType === "MULTIPLE_SELECT"
        ? question.correctAnswer?.split(",").includes(option.id)
        : question.correctAnswer === option.id;

    const wasSelected = isOptionSelected(question.uuid, option.id);

    if (isCorrect) return "correct";
    if (wasSelected && !isCorrect) return "incorrect";
    return "";
  };

  if (loading) {
    return (
      <div className="training-container">
        <LoadingSpinner message="Loading quiz..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="training-container">
        <div className="training-error">
          <div className="training-error-icon">!</div>
          <p className="training-error-message">{error}</p>
          <button className="btn btn-primary" onClick={loadQuiz}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!quiz || questions.length === 0) {
    return (
      <div className="training-container">
        <div className="training-empty">
          <div className="training-empty-icon">?</div>
          <p className="training-empty-message">
            No quiz available for this training
          </p>
          <button className="btn btn-ghost" onClick={onBack}>
            Back to Training
          </button>
        </div>
      </div>
    );
  }

  // Show results
  if (result) {
    const correctCount = result.questionResults
      ? result.questionResults.filter((qr) => qr.isCorrect).length
      : 0;
    const totalCount = result.questionResults
      ? result.questionResults.length
      : questions.length;

    return (
      <div className="training-container">
        <div className="quiz-section">
          <div className="quiz-results">
            <div
              className={`quiz-results-score ${result.passed ? "passed" : "failed"}`}
            >
              {result.score}%
            </div>
            <div className="quiz-results-message">
              {result.passed
                ? "Congratulations! You passed the quiz!"
                : "Keep learning! Review your answers below and try again."}
            </div>
            <div className="quiz-results-details">
              <span>Passing score: {quiz.passingScore}%</span>
              <span>•</span>
              <span>
                {correctCount} of {totalCount} correct
              </span>
              <span>•</span>
              <span>
                Time spent:{" "}
                {result.formattedTimeSpent ||
                  formatTime(result.timeSpentSeconds || 0)}
              </span>
            </div>

            {/* Question Review with Enhanced Feedback */}
            {result.questionResults && result.questionResults.length > 0 && (
              <div className="quiz-review-section">
                <h3 className="quiz-review-title">Review Your Answers</h3>

                {/* Summary Progress Bar */}
                <div className="quiz-review-summary">
                  <div className="quiz-review-progress-bar">
                    <div
                      className="quiz-review-progress-fill"
                      style={{ width: `${(correctCount / totalCount) * 100}%` }}
                    />
                  </div>
                  <div className="quiz-review-stats">
                    <span className="quiz-stat correct">
                      <span className="quiz-stat-icon">✓</span> {correctCount}{" "}
                      Correct
                    </span>
                    <span className="quiz-stat incorrect">
                      <span className="quiz-stat-icon">✗</span>{" "}
                      {totalCount - correctCount} Incorrect
                    </span>
                  </div>
                </div>

                {result.questionResults.map((qr, idx) => (
                  <div
                    key={idx}
                    className={`quiz-review-question ${qr.isCorrect ? "correct" : "incorrect"}`}
                  >
                    <div className="quiz-review-question-header">
                      <span className="quiz-review-question-number">
                        Question {idx + 1}
                      </span>
                      <span
                        className={`quiz-review-status ${qr.isCorrect ? "correct" : "incorrect"}`}
                      >
                        {qr.isCorrect ? (
                          <>
                            <span className="status-icon">✓</span> Correct
                          </>
                        ) : (
                          <>
                            <span className="status-icon">✗</span> Incorrect
                          </>
                        )}
                      </span>
                    </div>

                    <div className="quiz-review-question-text">
                      {qr.questionText}
                    </div>

                    <div className="quiz-review-answers">
                      {/* User's Answer */}
                      <div
                        className={`quiz-review-answer user-answer ${qr.isCorrect ? "correct" : "incorrect"}`}
                      >
                        <div className="quiz-review-answer-label">
                          Your Answer:
                        </div>
                        <div className="quiz-review-answer-text">
                          <span className="answer-indicator">
                            {qr.isCorrect ? "✓" : "✗"}
                          </span>
                          {qr.userAnswer || "(No answer provided)"}
                        </div>
                      </div>

                      {/* Correct Answer (shown if user was wrong) */}
                      {!qr.isCorrect && (
                        <div className="quiz-review-answer correct-answer">
                          <div className="quiz-review-answer-label">
                            Correct Answer:
                          </div>
                          <div className="quiz-review-answer-text">
                            <span className="answer-indicator correct">✓</span>
                            {qr.correctAnswer}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Explanation (always shown) */}
                    {qr.explanation && (
                      <div className="quiz-review-explanation">
                        <span className="explanation-icon">Tip:</span>
                        <span className="explanation-text">
                          {qr.explanation}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="quiz-results-actions">
              <button className="btn btn-ghost" onClick={onBack}>
                Back to Training
              </button>
              {result.passed && onComplete && (
                <button
                  className="btn btn-primary"
                  onClick={() => onComplete(result)}
                >
                  Continue
                </button>
              )}
              {!result.passed && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setResult(null);
                    setAnswers({});
                    setCurrentQuestionIndex(0);
                  }}
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const answeredCount = Object.keys(answers).filter((k) => answers[k]).length;
  const allAnswered = answeredCount === questions.length;

  return (
    <div className="training-container">
      <div className="quiz-section">
        {/* Quiz Header */}
        <div className="quiz-header">
          <h2>{quiz.title}</h2>
          {quiz.instructions && (
            <p className="quiz-instructions">{quiz.instructions}</p>
          )}
          <div className="quiz-meta">
            <span>
              Question {currentQuestionIndex + 1} of {questions.length}
            </span>
            <span>•</span>
            <span>
              {answeredCount} of {questions.length} answered
            </span>
            {timeRemaining !== null && (
              <>
                <span>•</span>
                <span
                  className={timeRemaining < 60 ? "quiz-timer-warning" : ""}
                >
                  Time: {formatTime(timeRemaining)}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Progress Dots */}
        <div className="quiz-progress-dots">
          {questions.map((q, idx) => (
            <button
              key={q.uuid}
              onClick={() => setCurrentQuestionIndex(idx)}
              className={`quiz-progress-dot ${answers[q.uuid] ? "answered" : ""} ${idx === currentQuestionIndex ? "current" : ""}`}
              title={`Question ${idx + 1}`}
            />
          ))}
        </div>

        {/* Current Question */}
        <div className="quiz-question">
          <div className="quiz-question-number">
            Question {currentQuestionIndex + 1}
            {currentQuestion.points > 1 && (
              <span className="quiz-question-points">
                ({currentQuestion.points} points)
              </span>
            )}
          </div>
          <div className="quiz-question-text">
            {currentQuestion.questionText}
          </div>

          <div className="quiz-options">
            {currentQuestion.options?.map((option) => (
              <label
                key={option.id}
                className={`quiz-option ${getOptionClass(currentQuestion, option, false)}`}
                onClick={() =>
                  handleAnswerSelect(currentQuestion.uuid, option.id)
                }
              >
                <input
                  type={
                    currentQuestion.questionType === "MULTIPLE_SELECT"
                      ? "checkbox"
                      : "radio"
                  }
                  name={`question-${currentQuestion.uuid}`}
                  checked={isOptionSelected(currentQuestion.uuid, option.id)}
                  onChange={() => {}}
                />
                <span className="quiz-option-text">{option.text}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="quiz-navigation">
          <button
            className="btn btn-ghost"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
          >
            ← Previous
          </button>

          {currentQuestionIndex < questions.length - 1 ? (
            <button className="btn btn-primary" onClick={handleNext}>
              Next →
            </button>
          ) : (
            <button
              className={`btn btn-primary ${allAnswered ? "quiz-submit-btn ready" : ""}`}
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
            >
              {submitting ? "Submitting..." : "Submit Quiz"}
            </button>
          )}
        </div>

        {/* Back button */}
        <div className="quiz-back-section">
          <button className="btn btn-ghost quiz-cancel-btn" onClick={onBack}>
            Cancel and Return to Training
          </button>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
